import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const PerformanceAnalytics = ({ 
  scoreHistory = [
    { chapter: 'Ch 1', score: 75, attempts: 1, timeSpent: 45 },
    { chapter: 'Ch 2', score: 85, attempts: 2, timeSpent: 52 },
    { chapter: 'Ch 3', score: 0, attempts: 0, timeSpent: 0 }
  ],
  skillBreakdown = [
    { skill: 'AI Fundamentals', score: 85, color: '#3B82F6' },
    { skill: 'Machine Learning', score: 78, color: '#6366F1' },
    { skill: 'Neural Networks', score: 0, color: '#E5E7EB' }
  ],
  learningPatterns = [
    { day: 'Mon', sessions: 2, minutes: 45 },
    { day: 'Tue', sessions: 1, minutes: 30 },
    { day: 'Wed', sessions: 3, minutes: 65 },
    { day: 'Thu', sessions: 1, minutes: 25 },
    { day: 'Fri', sessions: 2, minutes: 50 },
    { day: 'Sat', sessions: 1, minutes: 35 },
    { day: 'Sun', sessions: 0, minutes: 0 }
  ]
}) => {
  const averageScore = scoreHistory.reduce((sum, item) => sum + item.score, 0) / scoreHistory.filter(item => item.score > 0).length || 0;
  const totalAttempts = scoreHistory.reduce((sum, item) => sum + item.attempts, 0);
  const totalTimeSpent = scoreHistory.reduce((sum, item) => sum + item.timeSpent, 0);

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'var(--color-success)';
    if (score >= 80) return 'var(--color-secondary)';
    if (score >= 70) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getImprovementSuggestions = () => {
    const suggestions = [];
    
    if (averageScore < 80) {
      suggestions.push({
        icon: 'BookOpen',
        title: 'Review Content',
        description: 'Spend more time reviewing presentation materials before taking quizzes'
      });
    }
    
    if (totalAttempts > scoreHistory.length * 1.5) {
      suggestions.push({
        icon: 'Target',
        title: 'Focus on Understanding',
        description: 'Take time to understand concepts rather than rushing through attempts'
      });
    }
    
    const weeklyMinutes = learningPatterns.reduce((sum, day) => sum + day.minutes, 0);
    if (weeklyMinutes < 180) {
      suggestions.push({
        icon: 'Clock',
        title: 'Increase Study Time',
        description: 'Aim for at least 30 minutes of learning per day for better retention'
      });
    }

    return suggestions;
  };

  const suggestions = getImprovementSuggestions();

  return (
    <div className="space-y-6">
      {/* Analytics Header */}
      <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold font-heading text-text-primary">
              Performance Analytics
            </h3>
            <p className="mt-1 text-text-secondary">
              Detailed insights into your learning performance
            </p>
          </div>
          <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-primary-50">
            <Icon name="TrendingUp" size={16} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-sm font-medium text-primary">
              {Math.round(averageScore)}% Avg Score
            </span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="p-4 text-center rounded-lg bg-success-50">
            <div className="mb-1 text-2xl font-bold font-heading text-success">
              {Math.round(averageScore)}%
            </div>
            <div className="text-sm text-success-700">Average Score</div>
          </div>
          
          <div className="p-4 text-center rounded-lg bg-secondary-50">
            <div className="mb-1 text-2xl font-bold font-heading text-secondary">
              {totalAttempts}
            </div>
            <div className="text-sm text-secondary-700">Total Attempts</div>
          </div>
          
          <div className="p-4 text-center rounded-lg bg-accent-50">
            <div className="mb-1 text-2xl font-bold font-heading text-accent">
              {formatTime(totalTimeSpent)}
            </div>
            <div className="text-sm text-accent-700">Study Time</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Score Progress Chart */}
        <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
          <h4 className="mb-4 text-lg font-semibold font-heading text-text-primary">
            Score Progress
          </h4>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={scoreHistory.filter(item => item.score > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
                <XAxis 
                  dataKey="chapter" 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="score" 
                  fill="var(--color-secondary)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Learning Pattern Chart */}
        {/* <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
          <h4 className="mb-4 text-lg font-semibold font-heading text-text-primary">
            Weekly Learning Pattern
          </h4>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={learningPatterns}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
                <XAxis 
                  dataKey="day" 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="minutes" 
                  stroke="var(--color-primary)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      </div>

      {/* Skill Breakdown */}
      <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
        <h4 className="mb-4 text-lg font-semibold font-heading text-text-primary">
          Skill Assessment Breakdown
        </h4>
        <div className="space-y-4">
          {skillBreakdown.map((skill, index) => (
            <div key={index} className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-primary">{skill.skill}</span>
                  <span className="text-sm font-medium text-text-secondary">
                    {skill.score > 0 ? `${skill.score}%` : 'Not Started'}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-border-light">
                  <div 
                    className="h-2 rounded-full progress-animation"
                    style={{ 
                      width: `${skill.score}%`,
                      backgroundColor: skill.color
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Suggestions */}
      {suggestions.length > 0 && (
        <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
          <h4 className="mb-4 text-lg font-semibold font-heading text-text-primary">
            Improvement Suggestions
          </h4>
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-start p-4 space-x-3 rounded-lg bg-primary-50">
                <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100">
                  <Icon name={suggestion.icon} size={16} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <h5 className="mb-1 font-semibold font-heading text-primary-800">
                    {suggestion.title}
                  </h5>
                  <p className="text-sm text-primary-700">
                    {suggestion.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceAnalytics;